import { createElement, forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export type TypographyVariant =
  // Figma Design System Typography
  | 'h3-medium'
  | 'h5-medium'
  | 'h5-regular'
  | 'body-bold'
  | 'body-medium'
  | 'body-regular'
  | 'footnote-description'
  | 'footnote-monospace'
  // Legacy variants (keep for backward compatibility)
  | 'heading-white-center'
  | 'heading-white'
  | 'body-black-center'
  | 'body-primary'
  | 'body-gray-center'
  | 'body-gray-2'
  | 'body-text'
  | 'caption-gray';

type TypographyElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div';

export interface TypographyProps extends HTMLAttributes<HTMLElement> {
  variant: TypographyVariant;
  as?: TypographyElement;
  className?: string;
}

const variantClasses: Record<TypographyVariant, string> = {
  // Figma Design System Typography
  // h3-medium is 20px by product decision (Figma specifies 24px — deemed too
  // large in-app). Keep in sync with .text-h3-medium in styles/app.css.
  'h3-medium': 'font-roboto text-[20px] font-medium leading-[28px]',
  'h5-medium': 'font-roboto text-[16px] font-medium leading-[24px]',
  'h5-regular': 'font-roboto text-[16px] font-normal leading-[24px]',
  // body-* is 14/20 (Figma specifies 22px line-height) — compact-density
  // decision 2026-08-26. Keep in sync with .text-body-* in styles/app.css.
  'body-bold': 'font-roboto text-[14px] font-bold leading-[20px]',
  'body-medium': 'font-roboto text-[14px] font-medium leading-[20px]',
  'body-regular': 'font-roboto text-[14px] font-normal leading-[20px]',
  'footnote-description': 'font-roboto text-[12px] font-normal leading-[20px]',
  'footnote-monospace':
    'font-roboto-mono text-[12px] font-normal leading-[20px]',

  // Legacy variants (keep for backward compatibility)
  'heading-white-center':
    'text-2xl md:text-3xl font-bold text-white text-center',
  'heading-white': 'text-2xl md:text-3xl font-bold text-white',
  'body-black-center': 'text-base text-gray-900 text-center',
  'body-primary': 'text-base text-gray-900',
  'body-gray-center': 'text-base text-gray-600 text-center',
  'body-gray-2': 'text-base text-gray-500',
  'body-text': 'text-base text-white',
  'caption-gray': 'text-sm text-gray-500',
};

export const Typography = forwardRef<HTMLElement, TypographyProps>(
  ({ variant, as = 'p', className, children, ...props }, ref) => {
    return createElement(
      as,
      {
        ref,
        className: cn(variantClasses[variant], className),
        ...props,
      },
      children
    );
  }
);

Typography.displayName = 'Typography';
